import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

let scrollTriggerRegistered = false

function ensureScrollTrigger() {
  if (scrollTriggerRegistered) return true
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false

  gsap.registerPlugin(ScrollTrigger)
  scrollTriggerRegistered = true
  return true
}

function scheduleFrame(callback: () => void): number {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    return window.requestAnimationFrame(callback)
  }

  return window.setTimeout(callback, 16)
}

function cancelScheduledFrame(frameId: number) {
  if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(frameId)
    return
  }

  window.clearTimeout(frameId)
}

interface StackedCardItem {
  id: string
}

interface StackedCardsProps<T extends StackedCardItem> {
  items: T[]
  renderCard: (item: T, index: number) => React.ReactNode
  reducedMotion?: boolean
  className?: string
  itemClassName?: string
  cardClassName?: string
  scaleStep?: number
  minScale?: number
  stickyOffsetPx?: number
  liftAmountPx?: number
  scrub?: number
}

export default function StackedCards<T extends StackedCardItem>({
  items,
  renderCard,
  reducedMotion = false,
  className,
  itemClassName,
  cardClassName,
  scaleStep = 0.045,
  minScale = 0.72,
  stickyOffsetPx = 88,
  liftAmountPx = 64,
  scrub = 0.8,
}: StackedCardsProps<T>) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const segmentRefs = useRef<Array<HTMLDivElement | null>>([])
  const cardRefs = useRef<Array<HTMLDivElement | null>>([])
  const [useStaticLayout, setUseStaticLayout] = useState(reducedMotion)

  useEffect(() => {
    setUseStaticLayout(reducedMotion)
  }, [reducedMotion])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const cards = cardRefs.current.filter((card): card is HTMLDivElement => Boolean(card))
    cards.forEach(card => gsap.set(card, { clearProps: 'transform' }))

    if (useStaticLayout || items.length < 2) return
    if (!ensureScrollTrigger()) return

    const scrollerCandidate = root.closest('.scroll-snap-page')
    const scroller = scrollerCandidate instanceof HTMLElement ? scrollerCandidate : undefined
    const tweens: gsap.core.Tween[] = []
    let isDisposed = false
    let refreshFrame: number | null = null
    const viewport = typeof window !== 'undefined' ? window.visualViewport : null
    const fontsReady = document.fonts?.ready

    const scheduleRefresh = () => {
      if (refreshFrame !== null) cancelScheduledFrame(refreshFrame)

      refreshFrame = scheduleFrame(() => {
        refreshFrame = null
        if (!isDisposed) ScrollTrigger.refresh()
      })
    }

    cardRefs.current.forEach((card, index) => {
      const segment = segmentRefs.current[index]
      if (!card || !segment) return

      const targetScale = Math.max(minScale, 1 - (items.length - index) * scaleStep)
      const targetY = -liftAmountPx

      gsap.set(card, {
        scale: 1,
        y: 0,
        transformOrigin: 'center top',
      })

      const tween = gsap.to(card, {
        scale: targetScale,
        y: targetY,
        ease: 'none',
        scrollTrigger: {
          trigger: segment,
          scroller,
          start: `top top+=${stickyOffsetPx}`,
          end: `bottom top+=${stickyOffsetPx}`,
          scrub,
          invalidateOnRefresh: true,
        },
      })

      tweens.push(tween)
    })

    scheduleRefresh()

    window.addEventListener('resize', scheduleRefresh)
    window.addEventListener('orientationchange', scheduleRefresh)
    viewport?.addEventListener('resize', scheduleRefresh)
    fontsReady?.then(() => {
      if (!isDisposed) scheduleRefresh()
    }).catch(() => undefined)

    return () => {
      isDisposed = true
      window.removeEventListener('resize', scheduleRefresh)
      window.removeEventListener('orientationchange', scheduleRefresh)
      viewport?.removeEventListener('resize', scheduleRefresh)
      if (refreshFrame !== null) cancelScheduledFrame(refreshFrame)
      tweens.forEach(tween => {
        tween?.scrollTrigger?.kill()
        tween?.kill?.()
      })
      cards.forEach(card => gsap.set(card, { clearProps: 'transform' }))
    }
  }, [items, liftAmountPx, minScale, scaleStep, scrub, stickyOffsetPx, useStaticLayout])

  return (
    <div
      ref={rootRef}
      className={clsx('stacked-cards', useStaticLayout && 'stacked-cards--static', className)}
      data-testid="stacked-cards-root"
      style={{ ['--stack-sticky-top' as string]: `${stickyOffsetPx}px` }}
    >
      {items.map((item, index) => (
        <div
          key={item.id}
          ref={node => {
            segmentRefs.current[index] = node
          }}
          className={clsx('stacked-cards-item', itemClassName)}
          style={{ ['--stack-index' as string]: index, ['--stack-count' as string]: items.length, zIndex: index + 1 }}
        >
          <div className="stacked-cards-item-inner">
            <div className="stacked-cards-card-shell">
              <div
                ref={node => {
                  cardRefs.current[index] = node
                }}
                className={clsx('stacked-cards-card', cardClassName)}
              >
                {renderCard(item, index)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
